// Transcription Service for BeeVids
// Integrates with OpenAI Whisper API, Local Whisper (Desktop Service), and Pyannote for speaker diarization

class TranscriptionService {
  constructor(apiKeys) {
    this.whisperApiKey = apiKeys.whisper;
    this.desktopServiceURL = 'http://127.0.0.1:8001'; // New Python Local Agent Port
    this.pyannoteApiKey = apiKeys.pyannote;
    this.baseURL = 'https://api.openai.com/v1';
    this.pyannoteURL = 'https://api-inference.huggingface.co/models/pyannote/speaker-diarization-3.1';
  }

  // Upload file to Desktop Service
  async saveFileToTemp(file) {
    // Upload the file to Desktop Service via FormData
    // Desktop Service will save it to temp folder and return the path
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${this.desktopServiceURL}/upload-file`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to upload file: ${response.status}`);
      }
      
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'File upload failed');
      }
      
      return result.filePath;
    } catch (error) {
      console.error('Error uploading file to Desktop Service:', error);
      throw error;
    }
  }

  // Convert video file to audio for transcription
  async extractAudio(videoPath) {
    // In a real implementation, you'd use FFmpeg to extract audio
    // For now, we'll assume the video file can be sent directly to Whisper
    return videoPath;
  }

  // Transcribe audio using OpenAI Whisper API
  async transcribeWithWhisper(audioPath, language = 'auto') {
    try {
      const formData = new FormData();
      
      // In Electron, we need to handle file reading differently
      if (window.require) {
        const { ipcRenderer } = window.require('electron');
        const fs = window.require('fs');
        
        // Read the file as a blob
        const fileBuffer = fs.readFileSync(audioPath);
        const blob = new Blob([fileBuffer], { type: 'audio/mpeg' });
        formData.append('file', blob, 'audio.mp3');
      } else {
        // For web environment (development)
        throw new Error('File access not available in web environment');
      }

      formData.append('model', 'whisper-1');
      
      if (language !== 'auto') {
        formData.append('language', language);
      }
      
      formData.append('response_format', 'verbose_json');
      formData.append('timestamp_granularities[]', 'segment');

      const response = await fetch(`${this.baseURL}/audio/transcriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.whisperApiKey}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return this.formatWhisperResponse(result);
    } catch (error) {
      console.error('Whisper transcription error:', error);
      throw new Error(`Transcription failed: ${error.message}`);
    }
  }

  // Perform speaker diarization using Pyannote
  async performSpeakerDiarization(audioPath, segments = null) {
    try {
      // 1. Try to use Local Agent first (local, fast, and offline!)
      if (segments && segments.length > 0) {
        try {
          const formData = new FormData();
          formData.append('file_path', audioPath);
          formData.append('segments_json', JSON.stringify(segments));
          
          console.log('🎤 Calling Local Agent /diarize endpoint...');
          const response = await fetch(`${this.desktopServiceURL}/diarize`, {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.status === 'success') {
              console.log('✅ Local Agent diarization successful');
              return result.segments;
            }
          }
        } catch (e) {
          console.warn('Local Agent /diarize failed, skipping:', e.message);
        }
      }

      // 2. Fallback to Pyannote cloud API if key is provided
      if (!this.pyannoteApiKey) {
        console.warn('Pyannote API key not provided, skipping speaker diarization');
        return null;
      }

      const formData = new FormData();
      
      // Try to use Local Agent Pyannote model if possible
      try {
        const formData = new FormData();
        formData.append('file_path', audioPath);
        
        const response = await fetch(`${this.desktopServiceURL}/transcribe`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          return this.formatDiarizationResponse(result);
        }
      } catch (e) {
        console.warn('Local Agent diarization failed, skipping');
      }
      return null;
    } catch (error) {
      console.warn('Speaker diarization error:', error);
      return null;
    }
  }

  // Format Whisper API response into our transcript format
  formatWhisperResponse(whisperResult) {
    const segments = whisperResult.segments || [];
    
    return segments.map((segment, index) => ({
      id: `segment_${index + 1}`,
      startTime: segment.start,
      endTime: segment.end,
      text: segment.text.trim(),
      speaker: 'Speaker 1', // Will be updated by speaker diarization
      confidence: segment.confidence || 0.9,
      language: whisperResult.language || 'en',
      translations: {}
    }));
  }

  // Format speaker diarization response
  formatDiarizationResponse(diarizationResult) {
    // Convert Pyannote response to speaker segments
    // This is a simplified implementation
    return diarizationResult.speakers || [];
  }

  // Merge transcription with speaker diarization
  mergeSpeakerLabels(transcript, speakerSegments) {
    if (!speakerSegments || speakerSegments.length === 0) {
      return transcript;
    }

    return transcript.map(segment => {
      // Find the speaker for this time segment
      const speaker = this.findSpeakerForTimespan(
        segment.startTime, 
        segment.endTime, 
        speakerSegments
      );
      
      return {
        ...segment,
        speaker: speaker || segment.speaker
      };
    });
  }

  // Find speaker for a given timespan
  findSpeakerForTimespan(startTime, endTime, speakerSegments) {
    const midpoint = (startTime + endTime) / 2;
    
    for (const segment of speakerSegments) {
      if (midpoint >= segment.start && midpoint <= segment.end) {
        return segment.speaker;
      }
    }
    
    return null;
  }

  // Main transcription method that combines everything
  async transcribeVideo(videoPath, language = 'auto', options = {}) {
    try {
      const { 
        includeSpeakerDiarization = true,
        speakerDetection = { enabled: true, quality: 'standard' },
        speakerDetectionQuality = 'standard'
      } = options;
      
      // Step 1: Extract audio (in real implementation)
      const audioPath = await this.extractAudio(videoPath);
      
      // Step 2: Transcribe with Whisper
      const transcript = await this.transcribeWithWhisper(audioPath, language);
      
      // Step 3: Perform speaker diarization if requested
      let finalTranscript = transcript;
      let speakersDetected = 1;
      
      const shouldUseSpeakerDetection = includeSpeakerDiarization || speakerDetection.enabled;
      
      if (shouldUseSpeakerDetection) {
        console.log('🎤 API transcription with speaker detection:', speakerDetection.quality || speakerDetectionQuality);
        
        // 1. Try robust diarization (Local Agent / Pyannote)
        const diarizedResult = await this.performSpeakerDiarization(audioPath, transcript);
        if (diarizedResult) {
          if (diarizedResult.length > 0 && diarizedResult[0].text !== undefined) {
            // Local Agent returned full segments with speakers
            finalTranscript = diarizedResult;
          } else {
            // Cloud Pyannote returned segments, merge them
            finalTranscript = this.mergeSpeakerLabels(transcript, diarizedResult);
          }
          speakersDetected = this.countUniqueSpeakers(finalTranscript);
        } else {
          // 2. Naive client-side fallback
          const quality = speakerDetection.quality || speakerDetectionQuality || 'standard';
          const speakerResult = await this.applyBasicSpeakerDetection(transcript, quality);
          finalTranscript = speakerResult.transcript;
          speakersDetected = speakerResult.speakersDetected;
        }
      }
      
      return {
        success: true,
        transcript: finalTranscript,
        language: transcript[0]?.language || language,
        duration: Math.max(...transcript.map(s => s.endTime), 0),
        speakersDetected: speakersDetected
      };
    } catch (error) {
      console.error('Transcription service error:', error);
      return {
        success: false,
        error: error.message,
        transcript: []
      };
    }
  }

  // Apply basic speaker detection (similar to LocalWhisperService)
  async applyBasicSpeakerDetection(transcript, quality = 'standard') {
    try {
      console.log('🎤 Applying API-based speaker detection with quality:', quality);
      
      let speakersDetected = 1;
      let currentSpeaker = 'Speaker 1';
      
      const processedTranscript = transcript.map((segment, index) => {
        let assignedSpeaker = currentSpeaker;
        
        if (quality === 'basic') {
          // Simple detection: change speaker on long pauses (>2 seconds)
          if (index > 0) {
            const prevSegment = transcript[index - 1];
            const pauseDuration = segment.startTime - prevSegment.endTime;
            
            if (pauseDuration > 2.0) {
              const speakerNumber = currentSpeaker === 'Speaker 1' ? 2 : 1;
              currentSpeaker = `Speaker ${speakerNumber}`;
              assignedSpeaker = currentSpeaker;
              speakersDetected = Math.max(speakersDetected, speakerNumber);
            }
          }
        } else if (quality === 'standard') {
          // More sophisticated detection
          if (index > 0) {
            const prevSegment = transcript[index - 1];
            const pauseDuration = segment.startTime - prevSegment.endTime;
            const textLength = segment.text.length;
            const prevTextLength = prevSegment.text.length;
            
            const shouldChangeSpeaker = (
              pauseDuration > 1.5 ||
              (pauseDuration > 0.8 && Math.abs(textLength - prevTextLength) > 50) ||
              (segment.text.toLowerCase().includes('yes') && pauseDuration > 0.5) ||
              (segment.text.toLowerCase().includes('no') && pauseDuration > 0.5) ||
              (segment.text.toLowerCase().includes('okay') && pauseDuration > 0.5)
            );
            
            if (shouldChangeSpeaker) {
              const speakerNumber = currentSpeaker === 'Speaker 1' ? 2 : 1;
              currentSpeaker = `Speaker ${speakerNumber}`;
              assignedSpeaker = currentSpeaker;
              speakersDetected = Math.max(speakersDetected, speakerNumber);
            }
          }
        }
        
        return {
          ...segment,
          speaker: assignedSpeaker
        };
      });
      
      console.log(`🎤 API speaker detection complete: ${speakersDetected} speakers detected`);
      
      return {
        transcript: processedTranscript,
        speakersDetected: speakersDetected
      };
      
    } catch (error) {
      console.error('❌ API speaker detection failed:', error);
      return {
        transcript: transcript,
        speakersDetected: 1
      };
    }
  }

  // Count unique speakers in transcript
  countUniqueSpeakers(transcript) {
    const speakers = new Set(transcript.map(segment => segment.speaker));
    return speakers.size;
  }

  // Test API connectivity
  async testConnection() {
    try {
      const response = await fetch(`${this.baseURL}/models`, {
        headers: {
          'Authorization': `Bearer ${this.whisperApiKey}`,
        }
      });

      if (!response.ok) {
        throw new Error(`API test failed: ${response.status}`);
      }

      return { success: true, message: 'Whisper API connection successful' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  }

  // ✅ NEW: Check Desktop Transcription Service
  async checkDesktopService() {
    try {
      const response = await fetch(`${this.desktopServiceURL}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(2000) // 2 second timeout
      });
      
      if (!response.ok) {
        return { available: false, error: `Service returned ${response.status}` };
      }
      
      const data = await response.json();
      return { 
        available: true, 
        ...data 
      };
    } catch (error) {
      return { 
        available: false, 
        error: 'Local Agent not running. Please start VidoAI Local Agent (start.bat).',
        details: error.message
      };
    }
  }

  // ✅ NEW: Check Local Whisper installation
  async checkLocalWhisper() {
    try {
      console.log('🔍 Checking Local Whisper at:', `${this.desktopServiceURL}/check-whisper`);
      const response = await fetch(`${this.desktopServiceURL}/check-whisper`, {
        method: 'GET',
        signal: AbortSignal.timeout(10000) // 10 second timeout (increased from 5)
      });
      
      if (!response.ok) {
        console.error('❌ check-whisper returned status:', response.status);
        return { 
          ready: false, 
          error: `Service returned ${response.status}`,
          isConnectionError: true
        };
      }
      
      const data = await response.json();
      console.log('✅ check-whisper response:', data);
      console.log('🔍 ready value:', data.ready, 'type:', typeof data.ready);
      
      // Ensure we return the data with ready flag
      // Check both ready flag and installation status
      const isReady = data.ready === true || 
                     (data.python?.installed === true && data.whisper?.installed === true);
      
      console.log('✅ Final ready status:', isReady);
      
      return {
        ready: isReady,
        python: data.python,
        whisper: data.whisper,
        error: !isReady ? (data.error || 'Local Whisper is not installed') : null
      };
    } catch (error) {
      console.error('❌ Error checking Local Whisper:', error);
      return { 
        ready: false, 
        error: 'Cannot connect to desktop service',
        details: error.message,
        isConnectionError: true
      };
    }
  }

  // ✅ NEW: Transcribe with Local Whisper (Desktop Service)
  async transcribeWithLocalWhisper(audioFileOrPath, language = 'auto', model = 'base') {
    try {
      // Check if Desktop Service is available
      const serviceCheck = await this.checkDesktopService();
      if (!serviceCheck.available) {
        throw new Error(serviceCheck.error || 'Desktop service not available');
      }

      // Check if Local Whisper is ready
      const whisperCheck = await this.checkLocalWhisper();
      if (!whisperCheck.ready) {
        throw new Error(
          whisperCheck.error || 
          'Local Whisper not installed. Please install Python and Whisper in Desktop Service.'
        );
      }

      console.log('🎤 Using Local Whisper via Desktop Service...', {
        language,
        model,
        python: whisperCheck.python?.command,
        whisper: whisperCheck.whisper?.installed
      });

      // Get file path
      // Support both audio and video files
      let filePath;
      let isVideo = false;
      let tempFileCreated = false;
      
      if (audioFileOrPath instanceof File) {
        // Web App: File object - upload to Desktop Service
        // Desktop Service needs file path, so we'll upload it first
        try {
          console.log('📤 Uploading file to Desktop Service...', audioFileOrPath.name);
          filePath = await this.saveFileToTemp(audioFileOrPath);
          tempFileCreated = true;
          isVideo = /\.(mp4|avi|mov|mkv|webm|flv|wmv)$/i.test(audioFileOrPath.name);
          console.log('✅ File uploaded to Desktop Service:', filePath);
        } catch (error) {
          console.error('Error uploading file to Desktop Service:', error);
          throw new Error(`Failed to upload file for transcription: ${error.message}`);
        }
      } else if (typeof audioFileOrPath === 'string') {
        // File path
        // In Web App, we can send the path if it's an absolute path on the same machine as the Local Agent
        filePath = audioFileOrPath;
        isVideo = /\.(mp4|avi|mov|mkv|webm|flv|wmv)$/i.test(filePath);
      } else {
        console.error('❌ Invalid file format:', {
          type: typeof audioFileOrPath,
          isFile: audioFileOrPath instanceof File,
          value: audioFileOrPath
        });
        throw new Error('Invalid file format. Please provide file path (string) or File object.');
      }
      
      // Call Desktop Service API
      const formData = new FormData();
      
      if (audioFileOrPath instanceof File) {
        formData.append('file', audioFileOrPath);
      } else {
        // Send as file_path string
        formData.append('file_path', filePath);
      }

      formData.append('language', language);
      formData.append('model', model);

      console.log('🚀 Sending transcription request to Local Agent...', audioFileOrPath instanceof File ? 'Direct Upload' : `Path: ${filePath}`);
      const response = await fetch(`${this.desktopServiceURL}/transcribe`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Agent returned ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (result.status !== 'success') {
        throw new Error(result.message || 'Transcription failed');
      }

      // Format response to match our transcript format
      const formattedTranscript = (result.segments || []).map((segment, index) => ({
        id: segment.id || `segment_${index + 1}`,
        startTime: segment.start,
        endTime: segment.end,
        text: segment.text.trim(),
        speaker: segment.speaker || 'Speaker 1',
        confidence: 0.9,
        language: result.language || language,
        translations: {}
      }));

      return {
        success: true,
        transcript: formattedTranscript,
        language: result.language,
        duration: result.duration,
        text: result.text,
        localVideoPath: result.localVideoPath || filePath
      };
    } catch (error) {
      console.error('❌ Local Agent transcription error:', error);
      throw error;
    }
  }
}

export default TranscriptionService; 